<template>
  <div class="flex flex-col gap-1 w-full relative" ref="container">
    <label
      v-if="label"
      class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
    >
      {{ label }}
    </label>

    <!-- Select Box -->
    <div
      @click="toggleDropdown"
      :class="[
        'flex items-center justify-between cursor-pointer bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl transition-all hover:border-primary/50',
        isOpen
          ? 'ring-2 ring-primary/20 border-primary/50 bg-white dark:bg-dark-surface'
          : '',
      ]"
    >
      <span
        v-if="selectedLabel"
        class="text-sm font-bold truncate text-slate-700 dark:text-slate-200"
      >
        {{ selectedLabel }}
      </span>
      <span v-else class="text-sm text-slate-400 font-bold">
        {{ placeholder || $t("select_option") }}
      </span>

      <div class="flex items-center gap-1 flex-shrink-0 ml-2">
        <svg
          v-if="selectedLabel"
          @click.stop="clearSelection"
          class="w-4 h-4 text-slate-400 hover:text-red-500 transition-colors"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>

        <svg
          class="w-4 h-4 text-slate-400 transition-transform duration-300"
          :class="{ 'rotate-180 text-primary': isOpen }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
    </div>

    <!-- Dropdown — Teleported to body to escape overflow/z-index traps -->
    <Teleport to="body">
      <Transition name="dropdown-fade">
        <div
          v-if="isOpen"
          ref="dropdown"
          class="fixed bg-white dark:bg-dark-surface shadow-2xl rounded-2xl border border-slate-100 dark:border-white/10 overflow-hidden backdrop-blur-xl"
          :style="dropdownStyle"
          style="z-index: 99999"
        >
          <!-- Search Input -->
          <div class="p-2 border-b dark:border-white/5">
            <div class="relative">
              <input
                ref="searchInput"
                v-model="searchQuery"
                type="text"
                :placeholder="$t('search') + '...'"
                class="w-full bg-slate-50 dark:bg-dark-input border-none p-2 rounded-lg text-xs outline-none focus:ring-1 focus:ring-primary/30"
                @click.stop
              />
              <div class="absolute right-2 top-1.5 text-slate-400">
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <!-- Options List -->
          <div class="max-h-60 overflow-y-auto custom-scrollbar">
            <div
              v-for="option in filteredOptions"
              :key="option[valueKey]"
              @click="selectOption(option)"
              class="px-4 py-2.5 text-sm cursor-pointer hover:bg-primary/10 transition-colors flex items-center justify-between group"
              :class="{
                'bg-primary/5 text-primary': modelValue == option[valueKey],
              }"
            >
              <span class="font-bold">{{ getOptionLabel(option) }}</span>
              <svg
                v-if="modelValue == option[valueKey]"
                class="w-4 h-4 text-primary flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <div
              v-if="filteredOptions.length === 0"
              class="p-4 text-center text-xs text-slate-400 font-bold uppercase tracking-widest"
            >
              {{ $t("no_results_found") }}
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: "BaseSearchableSelect",
  props: {
    modelValue: [String, Number],
    options: {
      type: Array,
      default: () => [],
    },
    label: String,
    placeholder: String,
    valueKey: {
      type: String,
      default: "id",
    },
    labelKeyEn: {
      type: String,
      default: "name_en",
    },
    labelKeyAr: {
      type: String,
      default: "name_ar",
    },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      isOpen: false,
      searchQuery: "",
      dropdownStyle: {},
    };
  },
  computed: {
    selectedLabel() {
      const selected = this.options.find(
        (o) => o[this.valueKey] == this.modelValue
      );
      return selected ? this.getOptionLabel(selected) : null;
    },
    filteredOptions() {
      if (!this.searchQuery) return this.options;
      const query = this.searchQuery.toLowerCase();
      return this.options.filter((o) => {
        const en = (o[this.labelKeyEn] || "").toLowerCase();
        const ar = (o[this.labelKeyAr] || "").toLowerCase();
        const extra = o.username ? o.username.toLowerCase() : "";
        return (
          en.includes(query) || ar.includes(query) || extra.includes(query)
        );
      });
    },
  },
  methods: {
    clearSelection() {
      this.$emit("update:modelValue", "");
      this.$emit("change", "");
      this.isOpen = false;
    },
    toggleDropdown() {
      if (this.isOpen) {
        this.isOpen = false;
        return;
      }
      this.calculatePosition();
      this.isOpen = true;
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },

    calculatePosition() {
      const trigger = this.$refs.container;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 280; // max expected height
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const width = rect.width;
      const left = rect.left + window.scrollX;

      // Open upward if not enough space below and more space above
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        this.dropdownStyle = {
          width: `${width}px`,
          left: `${left}px`,
          bottom: `${viewportHeight - rect.top - window.scrollY}px`,
          top: "auto",
        };
      } else {
        this.dropdownStyle = {
          width: `${width}px`,
          left: `${left}px`,
          top: `${rect.bottom + window.scrollY + 4}px`,
          bottom: "auto",
        };
      }
    },

    selectOption(option) {
      this.$emit("update:modelValue", option[this.valueKey]);
      this.$emit("change", option[this.valueKey]);
      this.isOpen = false;
      this.searchQuery = "";
    },

    getOptionLabel(option) {
      if (option.username) return option.username;
      return this.$i18n.locale === "ar"
        ? option[this.labelKeyAr]
        : option[this.labelKeyEn];
    },

    handleClickOutside(event) {
      const container = this.$refs.container;
      const dropdown = this.$refs.dropdown;
      if (
        container &&
        !container.contains(event.target) &&
        dropdown &&
        !dropdown.contains(event.target)
      ) {
        this.isOpen = false;
      }
    },

    handleScrollOrResize() {
      if (this.isOpen) {
        this.calculatePosition();
      }
    },
  },

  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    window.addEventListener("scroll", this.handleScrollOrResize, true);
    window.addEventListener("resize", this.handleScrollOrResize);
  },

  unmounted() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("scroll", this.handleScrollOrResize, true);
    window.removeEventListener("resize", this.handleScrollOrResize);
  },
};
</script>

<style scoped>
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
}
.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
}
</style>
